import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { customerApi, apiError, useCustomer } from "../../context/CustomerContext";
import { ProfileFields, checkProfile } from "./ProfileFields";

const input = "mt-1 w-full h-12 rounded-[8px] bg-[#143823] border border-[#F6EEDC]/25 px-3 text-[#F6EEDC] outline-none focus:border-[#F2B441]";
const btn = "w-full h-13 py-3.5 rounded-full bg-[#8CD05A] text-[#0E2A1C] font-semibold hover:bg-[#F2B441] transition-colors disabled:opacity-50 flex items-center justify-center gap-2";

const LoginForm = ({ onForgot }) => {
  const { setUser } = useCustomer();
  const [v, setV] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { setUser(await customerApi.login(v)); toast.success("Sesión iniciada"); } catch (x) { setErr(apiError(x)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="login-form" className="space-y-3">
      <label className="block"><span className="text-xs text-[#C9D6C2]">Email *</span>
        <input data-testid="login-email" type="email" required autoComplete="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} className={input} /></label>
      <label className="block"><span className="text-xs text-[#C9D6C2]">Contraseña *</span>
        <input data-testid="login-password" type="password" required autoComplete="current-password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} className={input} /></label>
      {err && <p data-testid="login-error" className="text-sm text-[#F4A58F]">{err}</p>}
      <button type="submit" disabled={busy} data-testid="login-submit" className={btn}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Entrar</button>
      <button type="button" onClick={onForgot} data-testid="login-forgot" className="text-sm text-[#C9D6C2] underline underline-offset-4">¿Has olvidado tu contraseña?</button>
    </form>
  );
};

const RegisterForm = () => {
  const { setUser } = useCustomer();
  const [v, setV] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const ex = checkProfile(v, true);
    setErrors(ex);
    if (Object.keys(ex).length) return;
    setBusy(true); setErr("");
    try { setUser(await customerApi.register(v)); toast.success("Cuenta creada. ¡Bienvenido a la selva!"); } catch (x) { setErr(apiError(x)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} noValidate data-testid="register-form" className="space-y-4">
      <ProfileFields value={v} onChange={setV} errors={errors} withAuth />
      <p className="text-xs text-[#C9D6C2]">Todos los campos son obligatorios. Solo usamos tus datos para gestionar tus pedidos y envíos.</p>
      {err && <p data-testid="register-error" className="text-sm text-[#F4A58F]">{err}</p>}
      <button type="submit" disabled={busy} data-testid="register-submit" className={btn}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Crear cuenta</button>
    </form>
  );
};

const ForgotForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await customerApi.forgot(email).catch(() => {});
    setSent(true);
  };
  if (sent) return <p data-testid="forgot-sent" className="text-[#E3EADF]">Si hay una cuenta con ese email, te hemos enviado un enlace para cambiar la contraseña. Revisa también el correo no deseado.</p>;
  return (
    <form onSubmit={submit} data-testid="forgot-form" className="space-y-3">
      <label className="block"><span className="text-xs text-[#C9D6C2]">Email de tu cuenta *</span>
        <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} /></label>
      <button type="submit" data-testid="forgot-submit" className={btn}>Enviarme el enlace</button>
      <button type="button" onClick={onBack} className="text-sm text-[#C9D6C2] underline underline-offset-4">Volver a entrar</button>
    </form>
  );
};

export const AuthPanel = () => {
  const [tab, setTab] = useState("login");
  return (
    <div data-testid="auth-panel">
      {tab !== "forgot" && (
        <div className="grid grid-cols-2 gap-2 mb-6">
          {[["login", "Ya soy cliente"], ["register", "Soy nuevo"]].map(([id, l]) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`auth-tab-${id}`} aria-pressed={tab === id}
              className={`h-11 rounded-full border text-sm font-medium transition-colors ${tab === id ? "bg-[#F2B441] text-[#0E2A1C] border-[#F2B441]" : "border-[#F6EEDC]/25 hover:border-[#F2B441]"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "login" && <LoginForm onForgot={() => setTab("forgot")} />}
      {tab === "register" && <RegisterForm />}
      {tab === "forgot" && <ForgotForm onBack={() => setTab("login")} />}
    </div>
  );
};
